import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateRequest } from '@/actions/wizard/RequestWizardActions';
import useValidation from '@/hooks/wizard/useValidation';
import Step from '@/views/Wizards/RequestWizard/Wrappers/Step';
import { formatDate, normalizeDateToYYYYMMDD } from '@/utils/dateFormatter';

export default function MealTimingStep() {
    const dispatch = useDispatch();
    const { request } = useSelector((state) => state.requestWizard);
    const { completeStep } = useValidation();

    // get meal timings from Redux (defaults should be set in Redux)
    const mealTimings = request.mealTimings;

    // initialize meal timings with defaults when component mounts
    useEffect(() => {
        if (!request.mealDate || request.mealDate.length === 0) return;

        const currentTimings = mealTimings || {};
        const needsInitialization = request.mealDate.some(date => {
            const normalizedDate = normalizeDateToYYYYMMDD(date);
            return !currentTimings[normalizedDate];
        });

        if (needsInitialization) {
            const initialTimings = {};
            request.mealDate.forEach(date => {
                const normalizedDate = normalizeDateToYYYYMMDD(date);
                if (!currentTimings[normalizedDate]) {
                    initialTimings[normalizedDate] = { morning: false, lunch: true, evening: true };
                } else {
                    initialTimings[normalizedDate] = currentTimings[normalizedDate];
                }
            });
            dispatch(updateRequest('mealTimings', initialTimings));
        }
    }, []); // Run only once on mount

    // Auto-complete step when defaults already satisfy the requirement
    useEffect(() => {
        if (!request.mealDate || request.mealDate.length === 0) return;

        const allDatesHaveSelection = request.mealDate.every((date) => {
            const normalizedDate = normalizeDateToYYYYMMDD(date);
            const dateTimings = mealTimings[normalizedDate];
            return dateTimings && Object.values(dateTimings).some((selected) => selected);
        });

        if (allDatesHaveSelection) {
            completeStep();
        } else {
            completeStep(false);
        }
    }, [request.mealDate, mealTimings]);

    return (
        <Step>
            <h4 className="step-title">When would you like your meals?</h4>
            <p className="mb-4 text-center step-info">
                You can always change this later
            </p>

            <div className="meal-timing-grid">
                {/* Header */}
                <div className="grid grid-cols-4 gap-4 mb-4 font-medium text-center">
                    <div></div>
                    <div className="text-gray-600">Morning</div>
                    <div className="text-gray-600">Lunch</div>
                    <div className="text-gray-600">Evening</div>
                </div>

                {/* Date rows */}
                {request.mealDate?.map((date, index) => {
                    const normalizedDate = normalizeDateToYYYYMMDD(date);
                    return (
                        <div key={normalizedDate} className="grid items-center grid-cols-4 gap-4 py-3 border-b border-gray-100 last:border-b-0">
                            <div className="font-medium text-gray-800">
                                {formatDate(date)}
                            </div>

                            {['morning', 'lunch', 'evening'].map((timing) => (
                                <div key={timing} className="flex justify-center">
                                    <label className="cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only"
                                            checked={mealTimings[normalizedDate]?.[timing] || false}
                                            onChange={(e) => {
                                                const dateTimings = mealTimings[normalizedDate];
                                                const updatedTimings = {
                                                    ...mealTimings,
                                                    [normalizedDate]: {
                                                        ...dateTimings,
                                                        [timing]: e.target.checked
                                                    }
                                                };

                                                dispatch(updateRequest('mealTimings', updatedTimings));

                                                // Check if at least one timing is selected for EACH date
                                                const allDatesHaveSelection = request.mealDate.every(d => {
                                                    const normDate = normalizeDateToYYYYMMDD(d);
                                                    const dateTimings = updatedTimings[normDate];
                                                    return dateTimings && Object.values(dateTimings).some(selected => selected);
                                                });

                                                if (allDatesHaveSelection) {
                                                    completeStep();
                                                } else {
                                                    // Clear completion if not all dates have selections
                                                    completeStep(false);
                                                }
                                            }}
                                        />
                                        <div className={`w-6 h-6 border-2 rounded flex items-center justify-center transition-colors ${mealTimings[normalizedDate]?.[timing]
                                                ? 'bg-purple-600 border-purple-600'
                                                : 'border-gray-300 hover:border-purple-400'
                                            }`}>
                                            {mealTimings[normalizedDate]?.[timing] && (
                                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </div>
                                    </label>
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>

        </Step>
    );
}
